import { useCallback, useEffect, useRef, useState } from "react";
import intl from "react-intl-universal";
import { Link } from "react-router-dom";
import { useToast } from "../../../../../Hooks/toast";
import { useBaasCapabilities } from "../../../../../Hooks/useBaasCapabilities";
import { useBaasPlatformCreateAccess } from "../../../../../Hooks/useBaasPlatformCreateAccess";
import {
  BAAS_ENTITY_FEATURE_OPTIONS,
  fetchBaasEntityFeatures,
  setBaasEntityFeatures,
} from "../../../../../Services/baasEntityFeaturesApi";
import { formatBaasConsoleConflictMessage } from "../../../../../Services/baasConsoleClient";
import { SectionHeading } from "./baasEntidadeCreateFields";

const SETTINGS_PATH = "/baas/configuracoes";

const featureLabelKey = (code) => `baas.instances.createPage.feature.${code}`;
const featureHintKey = (code) => `baas.instances.createPage.featureHint.${code}`;

const sourceLabelKey = {
  instance: "baas.users.entityPermissions.source.instance",
  entity: "baas.users.entityPermissions.source.entity",
  denied_by_instance: "baas.users.entityPermissions.source.deniedByInstance",
};

const sourceToneClass = {
  instance: "text-sky-700 dark:text-sky-300",
  entity: "text-amber-700 dark:text-amber-300",
  denied_by_instance: "text-gray-400 dark:text-gray-500",
};

const productsToEnabledMap = (products = []) =>
  Object.fromEntries(
    BAAS_ENTITY_FEATURE_OPTIONS.map((option) => {
      const product = products.find((item) => item.code === option.code);
      return [option.code, product ? Boolean(product.enabled) : option.defaultEnabled !== false];
    }),
  );

const productsToSourceMap = (products = []) =>
  Object.fromEntries(
    BAAS_ENTITY_FEATURE_OPTIONS.map((option) => {
      const product = products.find((item) => item.code === option.code);
      return [option.code, product?.source ?? "instance"];
    }),
  );

const FeatureToggleRow = ({
  label,
  hint,
  source,
  checked,
  onChange,
  disabled,
  footer = null,
}) => (
  <div
    className={`rounded-lg border px-3 py-2.5 dark:border-brand-dark-border ${
      disabled
        ? "border-gray-200 bg-gray-50/80 dark:bg-brand-dark-elevated/30"
        : "border-gray-200 dark:border-brand-dark-border"
    }`}
  >
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="text-sm font-medium text-brand-dark dark:text-gray-100">{label}</p>
        {hint ? <p className="text-xs text-gray-500 dark:text-gray-400">{hint}</p> : null}
        {source ? (
          <p
            className={`mt-0.5 text-[11px] uppercase tracking-wide ${
              sourceToneClass[source] ?? sourceToneClass.instance
            }`}
          >
            {intl.get(sourceLabelKey[source] ?? sourceLabelKey.instance)}
          </p>
        ) : null}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-disabled={disabled}
        className={`relative h-6 w-11 shrink-0 rounded-full transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e7a73f]/40 ${
          checked ? "bg-[#e7a73f]" : "bg-gray-300 dark:bg-brand-dark-border"
        } ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
        onClick={() => {
          if (!disabled) onChange(!checked);
        }}
        disabled={disabled}
      >
        <span
          className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition ${
            checked ? "left-5" : "left-0.5"
          }`}
        />
      </button>
    </div>
    {footer}
  </div>
);

/**
 * Produtos da entidade — per-correntista gates via GET|PUT …/entities/{id}/features.
 * Primary ops surface (Entidades detail); Usuários instance modal still edits the person’s pers_.
 */
export const BaasEntidadeProductsPanel = ({ instance, entityId }) => {
  const { addToast } = useToast();
  const { can, isMaster } = useBaasCapabilities();
  const { isValutConfigAdmin, loading: valutAdminLoading } = useBaasPlatformCreateAccess();

  // Mirror Usuários write access + Valut matrix (PUT …/features elevates partner key).
  // Operador must not edit — require users.manage / entities.update / Valut admin
  // (do not use !isReadOnly alone; transactions.write makes Operador non-read-only).
  const canEditFeatures =
    Boolean(isValutConfigAdmin) ||
    isMaster ||
    can("users.manage") ||
    can("entities.update");

  const [enabledMap, setEnabledMap] = useState({});
  const [sourceMap, setSourceMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const dirtyRef = useRef(false);

  useEffect(() => {
    dirtyRef.current = dirty;
  }, [dirty]);

  useEffect(() => {
    let cancelled = false;

    if (!instance || !entityId) {
      setEnabledMap({});
      setSourceMap({});
      setLoadError("");
      setLoading(false);
      setDirty(false);
      dirtyRef.current = false;
      return undefined;
    }

    setLoading(true);
    setLoadError("");
    setDirty(false);
    dirtyRef.current = false;

    (async () => {
      try {
        const response = await fetchBaasEntityFeatures(instance, entityId);
        if (cancelled || dirtyRef.current) return;
        setEnabledMap(productsToEnabledMap(response.products));
        setSourceMap(productsToSourceMap(response.products));
      } catch (error) {
        if (!cancelled && !dirtyRef.current) {
          setEnabledMap({});
          setSourceMap({});
          setLoadError(
            error?.detail || error?.message || intl.get("baas.users.entityPermissions.loadError"),
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [instance, entityId]);

  const toggleFeature = useCallback(
    (code, enabled) => {
      if (!canEditFeatures) return;
      setEnabledMap((current) => ({ ...current, [code]: enabled }));
      setSourceMap((current) => {
        const previous = current[code];
        if (previous === "denied_by_instance" && !isValutConfigAdmin) {
          return current;
        }
        return { ...current, [code]: "entity" };
      });
      setDirty(true);
      dirtyRef.current = true;
    },
    [canEditFeatures, isValutConfigAdmin],
  );

  const handleSave = useCallback(async () => {
    if (!canEditFeatures || !instance || !entityId || saving || !dirty) return;

    const toSave = BAAS_ENTITY_FEATURE_OPTIONS.filter(
      (option) => sourceMap[option.code] === "entity",
    ).map((option) => ({
      feature_code: option.code,
      enabled: Boolean(enabledMap[option.code]),
    }));

    if (toSave.length === 0) {
      setDirty(false);
      dirtyRef.current = false;
      return;
    }

    setSaving(true);
    try {
      const response = await setBaasEntityFeatures(instance, entityId, toSave);
      setEnabledMap(productsToEnabledMap(response.products));
      setSourceMap(productsToSourceMap(response.products));
      setDirty(false);
      dirtyRef.current = false;
      addToast({
        type: "success",
        title: intl.get("baas.entidades.detail.products.saveSuccess"),
      });
    } catch (error) {
      addToast({
        type: "error",
        title: intl.get("baas.entidades.detail.products.saveError"),
        description: formatBaasConsoleConflictMessage(
          error,
          error?.detail || error?.message || null,
        ),
      });
    } finally {
      setSaving(false);
    }
  }, [
    addToast,
    canEditFeatures,
    dirty,
    enabledMap,
    entityId,
    instance,
    saving,
    sourceMap,
  ]);

  if (!instance || !entityId) return null;

  const visibleOptions = BAAS_ENTITY_FEATURE_OPTIONS.filter((option) => {
    const source = sourceMap[option.code] ?? "instance";
    return source !== "denied_by_instance";
  });

  return (
    <section className="app-card relative shrink-0 p-6" aria-label={intl.get("baas.users.entityPermissions.sectionTitle")}>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <SectionHeading>{intl.get("baas.users.entityPermissions.sectionTitle")}</SectionHeading>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {intl.get("baas.entidades.detail.products.hint", {
              instance: instance?.name ?? "",
            })}
          </p>
          <p className="mt-1 font-mono text-xs text-gray-400 dark:text-gray-500">{entityId}</p>
        </div>
        {canEditFeatures && dirty ? (
          <button
            type="button"
            className="btn-primary w-auto shrink-0 !text-xs"
            disabled={saving || loading}
            onClick={() => {
              void handleSave();
            }}
          >
            {saving
              ? intl.get("baas.entidades.detail.products.saving")
              : intl.get("baas.entidades.detail.products.save")}
          </button>
        ) : null}
      </div>

      {!canEditFeatures && !valutAdminLoading ? (
        <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
          {intl.get("baas.entidades.detail.products.readOnlyHint")}
        </p>
      ) : null}

      {canEditFeatures ? (
        <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
          {intl.get("baas.users.entityPermissions.editHint")}
        </p>
      ) : null}

      {loadError ? (
        <p
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300"
        >
          {loadError}
        </p>
      ) : null}

      {loading ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {intl.get("baas.users.entityPermissions.loadingFeatures")}
        </p>
      ) : !loadError ? (
        <div className="grid gap-2 sm:grid-cols-2">
          {(sourceMap.cards ?? "instance") !== "denied_by_instance" ? (
            <p className="sm:col-span-2 rounded-lg border border-[#e7a73f]/40 bg-[#e7a73f]/10 px-3 py-2 text-xs text-brand-dark dark:text-gray-100">
              {intl.get("baas.users.entityPermissions.cardsIncludes")}
            </p>
          ) : null}
          {visibleOptions.map((option) => {
            const source = sourceMap[option.code] ?? "instance";
            const denied = source === "denied_by_instance";
            const switchDisabled =
              saving ||
              !canEditFeatures ||
              (denied && !isValutConfigAdmin);
            return (
              <FeatureToggleRow
                key={option.code}
                label={intl.get(featureLabelKey(option.code))}
                hint={intl.get(featureHintKey(option.code))}
                source={source}
                checked={Boolean(enabledMap[option.code])}
                disabled={switchDisabled}
                onChange={(enabled) => toggleFeature(option.code, enabled)}
                footer={
                  denied ? (
                    <p className="mt-2 text-[11px] leading-snug text-gray-500 dark:text-gray-400">
                      {intl.get("baas.users.entityPermissions.deniedHint")}{" "}
                      <Link
                        to={SETTINGS_PATH}
                        className="font-medium text-[#e7a73f] underline-offset-2 hover:underline"
                      >
                        {intl.get("baas.users.entityPermissions.openSettings")}
                      </Link>
                      {isValutConfigAdmin
                        ? ` ${intl.get("baas.users.entityPermissions.adminDeniedHint")}`
                        : null}
                    </p>
                  ) : source === "instance" && canEditFeatures ? (
                    <p className="mt-2 text-[11px] text-gray-400">
                      {intl.get("baas.users.entityPermissions.inheritedHint")}
                    </p>
                  ) : null
                }
              />
            );
          })}
          {visibleOptions.length === 0 ? (
            <p className="sm:col-span-2 text-sm text-gray-500 dark:text-gray-400">
              {intl.get("baas.entidades.detail.products.emptyInstance")}
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
};
